import { useEffect, useState } from "react";
import { fetchPlaceDisplayDetails } from "../utils/googlePlaces";

/** List-row thumbnail size — avoid pulling full 800px media for every explore row. */
const LIST_PHOTO_OPTIONS = {
  maxHeightPx: 200,
  maxWidthPx: 200,
  photoLimit: 1,
};

/**
 * Lazy-load Google display name + first photo for an explore-list row.
 * Falls back to nulls on missing/synthetic ids or fetch failure so the row can keep DB fields.
 *
 * @param {string|null|undefined} placeId - Google Place ID
 * @returns {{ name: string|null, photoUrl: string|null, isLoading: boolean }}
 */
export function useGooglePlaceDisplay(placeId) {
  const [name, setName] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const reset = () => {
      setName(null);
      setPhotoUrl(null);
      setIsLoading(false);
    };

    if (!placeId || typeof placeId !== "string") {
      reset();
      return undefined;
    }

    // Synthetic House Pick IDs are not real Google Place IDs.
    if (placeId.startsWith("housepick-ai-")) {
      reset();
      return undefined;
    }

    setIsLoading(true);
    fetchPlaceDisplayDetails(placeId, LIST_PHOTO_OPTIONS)
      .then((details) => {
        if (cancelled) return;
        setName(typeof details?.name === "string" && details.name.trim() ? details.name : null);
        const firstPhoto =
          Array.isArray(details?.photoUrls) && typeof details.photoUrls[0] === "string"
            ? details.photoUrls[0]
            : null;
        setPhotoUrl(firstPhoto);
      })
      .catch(() => {
        if (cancelled) return;
        reset();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [placeId]);

  return { name, photoUrl, isLoading };
}
